import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link2, Plus, Pencil, Trash2, Zap, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ApiConnection } from "@shared/schema";

const connectionFormSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  description: z.string().optional(),
  baseUrl: z.string().url("Ange en giltig URL"),
  authType: z.enum(["none", "api_key", "bearer", "basic"]),
  apiKey: z.string().optional(),
  apiKeyHeader: z.string().optional(),
  bearerToken: z.string().optional(),
  basicUsername: z.string().optional(),
  basicPassword: z.string().optional(),
  customHeaders: z.string().optional(),
  isActive: z.boolean().default(true),
});

type ConnectionFormValues = z.infer<typeof connectionFormSchema>;

const authTypeLabels: Record<string, string> = {
  none: "Ingen",
  api_key: "API-nyckel",
  bearer: "Bearer Token",
  basic: "Basic Auth",
};

export default function AdminConnections() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<ApiConnection | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const { data: connections, isLoading } = useQuery<ApiConnection[]>({
    queryKey: ["/api/admin/connections"],
  });

  const form = useForm<ConnectionFormValues>({
    resolver: zodResolver(connectionFormSchema),
    defaultValues: {
      name: "",
      description: "",
      baseUrl: "",
      authType: "api_key",
      apiKey: "",
      apiKeyHeader: "Authorization",
      bearerToken: "",
      basicUsername: "",
      basicPassword: "",
      customHeaders: "",
      isActive: true,
    },
  });

  const authType = form.watch("authType");

  const createMutation = useMutation({
    mutationFn: async (data: ConnectionFormValues) => {
      let customHeaders: Record<string, string> | undefined;
      if (data.customHeaders?.trim()) {
        try {
          customHeaders = JSON.parse(data.customHeaders);
        } catch {
          customHeaders = undefined;
        }
      }
      const res = await apiRequest("POST", "/api/admin/connections", {
        ...data,
        customHeaders,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/connections"] });
      setDialogOpen(false);
      form.reset();
      toast({ title: "Koppling skapad" });
    },
    onError: () => {
      toast({ title: "Kunde inte skapa koppling", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ConnectionFormValues }) => {
      let customHeaders: Record<string, string> | undefined;
      if (data.customHeaders?.trim()) {
        try {
          customHeaders = JSON.parse(data.customHeaders);
        } catch {
          customHeaders = undefined;
        }
      }
      const res = await apiRequest("PATCH", `/api/admin/connections/${id}`, {
        ...data,
        customHeaders,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/connections"] });
      setDialogOpen(false);
      setEditingConnection(null);
      form.reset();
      toast({ title: "Koppling uppdaterad" });
    },
    onError: () => {
      toast({ title: "Kunde inte uppdatera koppling", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/connections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/connections"] });
      setDeleteId(null);
      toast({ title: "Koppling raderad" });
    },
    onError: () => {
      toast({ title: "Kunde inte radera koppling", variant: "destructive" });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      setTestingId(id);
      const res = await apiRequest("POST", `/api/admin/connections/${id}/test`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/connections"] });
      setTestingId(null);
      if (data.success) {
        toast({ title: "Anslutningen lyckades", description: `Status: ${data.status} ${data.statusText}` });
      } else {
        toast({ title: "Anslutningen misslyckades", description: data.statusText || "Okänt fel", variant: "destructive" });
      }
    },
    onError: () => {
      setTestingId(null);
      toast({ title: "Kunde inte testa koppling", variant: "destructive" });
    },
  });

  const openCreateDialog = () => {
    setEditingConnection(null);
    form.reset({
      name: "",
      description: "",
      baseUrl: "",
      authType: "api_key",
      apiKey: "",
      apiKeyHeader: "Authorization",
      bearerToken: "",
      basicUsername: "",
      basicPassword: "",
      customHeaders: "",
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (conn: ApiConnection) => {
    setEditingConnection(conn);
    form.reset({
      name: conn.name,
      description: conn.description || "",
      baseUrl: conn.baseUrl,
      authType: conn.authType as ConnectionFormValues["authType"],
      apiKey: conn.apiKey || "",
      apiKeyHeader: conn.apiKeyHeader || "Authorization",
      bearerToken: conn.bearerToken || "",
      basicUsername: conn.basicUsername || "",
      basicPassword: conn.basicPassword || "",
      customHeaders: conn.customHeaders ? JSON.stringify(conn.customHeaders, null, 2) : "",
      isActive: conn.isActive ?? true,
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: ConnectionFormValues) => {
    if (editingConnection) {
      updateMutation.mutate({ id: editingConnection.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const getTestStatusBadge = (conn: ApiConnection) => {
    if (!conn.lastTestStatus) return null;
    if (conn.lastTestStatus === "success") {
      return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle2 className="h-3 w-3 mr-1" />OK</Badge>;
    }
    return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20"><XCircle className="h-3 w-3 mr-1" />Fel</Badge>;
  };

  const formatDate = (dateStr: string | Date | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("sv-SE", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6" data-testid="page-connections">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-connections-title">API-kopplingar</h1>
          <p className="text-muted-foreground">Hantera kopplingar till externa system och API:er</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="button-add-connection">
          <Plus className="h-4 w-4 mr-2" />
          Ny koppling
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Kopplingar</CardTitle>
          <CardDescription>{connections?.length || 0} kopplingar konfigurerade</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : connections && connections.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Autentisering</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Senast testad</TableHead>
                    <TableHead className="text-right">Åtgärder</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {connections.map((conn) => (
                    <TableRow key={conn.id} data-testid={`row-connection-${conn.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium" data-testid={`text-connection-name-${conn.id}`}>{conn.name}</p>
                          {conn.description && (
                            <p className="text-xs text-muted-foreground">{conn.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{conn.baseUrl}</code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{authTypeLabels[conn.authType] || conn.authType}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {conn.isActive ? (
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Aktiv</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-muted text-muted-foreground">Inaktiv</Badge>
                          )}
                          {getTestStatusBadge(conn)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {conn.lastTestedAt ? (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(conn.lastTestedAt)}
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => testMutation.mutate(conn.id)}
                            disabled={testingId === conn.id}
                            data-testid={`button-test-connection-${conn.id}`}
                          >
                            {testingId === conn.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Zap className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEditDialog(conn)}
                            data-testid={`button-edit-connection-${conn.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteId(conn.id)}
                            data-testid={`button-delete-connection-${conn.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center" data-testid="connections-empty-state">
              <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">Inga kopplingar konfigurerade</p>
              <p className="text-sm text-muted-foreground mb-4">
                Skapa en koppling för att ansluta till en extern API.
              </p>
              <Button onClick={openCreateDialog} data-testid="button-add-connection-empty">
                <Plus className="h-4 w-4 mr-2" />
                Skapa koppling
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingConnection(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="text-connection-dialog-title">
              {editingConnection ? "Redigera koppling" : "Ny API-koppling"}
            </DialogTitle>
            <DialogDescription>
              {editingConnection ? "Uppdatera inställningar för API-kopplingen" : "Konfigurera en ny koppling till en extern API"}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Namn</FormLabel>
                    <FormControl>
                      <Input placeholder="t.ex. Fortnox, Woocommerce" {...field} data-testid="input-connection-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beskrivning</FormLabel>
                    <FormControl>
                      <Input placeholder="Valfri beskrivning" {...field} data-testid="input-connection-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="baseUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bas-URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://api.example.com/v1" {...field} data-testid="input-connection-url" />
                    </FormControl>
                    <FormDescription>Grund-URL för alla API-anrop</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="authType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Autentisering</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-auth-type">
                          <SelectValue placeholder="Välj autentiseringstyp" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Ingen autentisering</SelectItem>
                        <SelectItem value="api_key">API-nyckel</SelectItem>
                        <SelectItem value="bearer">Bearer Token</SelectItem>
                        <SelectItem value="basic">Basic Auth (användarnamn/lösenord)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {authType === "api_key" && (
                <>
                  <FormField
                    control={form.control}
                    name="apiKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>API-nyckel</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Din API-nyckel" {...field} data-testid="input-api-key" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="apiKeyHeader"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Header-namn</FormLabel>
                        <FormControl>
                          <Input placeholder="Authorization" {...field} data-testid="input-api-key-header" />
                        </FormControl>
                        <FormDescription>Vilket HTTP-header nyckeln skickas i</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {authType === "bearer" && (
                <FormField
                  control={form.control}
                  name="bearerToken"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bearer Token</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Din bearer token" {...field} data-testid="input-bearer-token" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {authType === "basic" && (
                <>
                  <FormField
                    control={form.control}
                    name="basicUsername"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Användarnamn</FormLabel>
                        <FormControl>
                          <Input placeholder="Användarnamn" {...field} data-testid="input-basic-username" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="basicPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lösenord</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Lösenord" {...field} data-testid="input-basic-password" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="customHeaders"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Extra headers (JSON)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={'{"X-Custom-Header": "value"}'}
                        className="font-mono text-sm"
                        rows={3}
                        {...field}
                        data-testid="input-custom-headers"
                      />
                    </FormControl>
                    <FormDescription>Valfria extra HTTP-headers som JSON-objekt</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel>Aktiv</FormLabel>
                      <FormDescription>Aktivera eller inaktivera kopplingen</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-connection-active" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-connection">
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save-connection"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {editingConnection ? "Spara" : "Skapa"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Radera koppling?</AlertDialogTitle>
            <AlertDialogDescription>
              Denna åtgärd kan inte ångras. Alla inställningar för denna koppling kommer att tas bort.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              data-testid="button-confirm-delete"
            >
              Radera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
